import { forwardRef, useCallback, useState } from 'react';
import { isHideMetrics, isShopifyPreview, isTestUser } from '@/modules/api';
import { bridgeAtom, chatIdAtom, mlVersionAtom, useValue } from '@/store';
import { Block, Button, Copied, Label, SuccessIcon, Wrapper } from './Maintenance.styles';

export const Maintenance = forwardRef<HTMLDivElement>((_, ref) => {
  const [isGroupsOpen, setIsGroupsOpen] = useState(false);
  const [isVersionsOpen, setIsVersionsOpen] = useState(false);
  const { abTestConfig, onChangeTestGroup } = useValue(bridgeAtom);
  const mlVersion = useValue(mlVersionAtom);
  const chatId = useValue(chatIdAtom);
  const isTest = isTestUser();
  const isPreview = isShopifyPreview();
  const isMetricsHidden = isHideMetrics();

  const [isCopied, setIsCopied] = useState(false);
  const clientVersion = `v.${__APP_VERSION__} (${__APP_ENVIRONMENT__})`;
  const copyText = useCallback((copyText: string) => {
    navigator.clipboard.writeText(`${copyText}`);
    setIsGroupsOpen(false);
    setIsVersionsOpen(false);
    setIsCopied(true);

    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  }, []);

  const onClick = useCallback(() => setIsGroupsOpen(true), [setIsGroupsOpen]);

  const changeTestGroup = useCallback(
    (newGroupName: string) => {
      if (!abTestConfig?.client.uuid) {
        return;
      }

      localStorage.setItem(abTestConfig.client.uuid, newGroupName);
      onChangeTestGroup?.(newGroupName);
    },
    [abTestConfig, onChangeTestGroup]
  );

  if (!isTest || isPreview || isMetricsHidden) {
    return null;
  }

  if (isCopied) {
    return (
      <Wrapper ref={ref}>
        <Block>
          <Block>
            <SuccessIcon />
            <Copied>Copied</Copied>
          </Block>
        </Block>
      </Wrapper>
    );
  }

  return (
    <Wrapper ref={ref}>
      {isGroupsOpen && (
        <Block justifyContent="space-between" width={'100%'}>
          <Button textTransform="uppercase" fontSize={'9px'} onClick={() => setIsGroupsOpen(false)}>
            back
          </Button>

          <Block>
            <Label>Select group:</Label>
            <Block gap={'12px'}>
              {(abTestConfig?.client || abTestConfig?.ml)?.groupsList?.map((groupName) => (
                <Button
                  key={groupName}
                  isActive={groupName === (abTestConfig?.client || abTestConfig?.ml)?.group}
                  onClick={() => changeTestGroup(groupName)}
                >
                  {groupName}
                </Button>
              ))}
            </Block>
          </Block>
        </Block>
      )}
      {isVersionsOpen && (
        <Block justifyContent="space-between" width={'100%'}>
          <Button textTransform="uppercase" fontSize={'9px'} onClick={() => setIsVersionsOpen(false)}>
            back
          </Button>

          <Block gap={'12px'}>
            <Block>
              <Label>Client:</Label>
              <Button title={clientVersion} fontSize={'10px'} onClick={() => copyText(clientVersion)}>
                {clientVersion}
              </Button>
            </Block>
            {mlVersion && (
              <Block>
                <Label>ML:</Label>
                <Button title={`v.${mlVersion}`} fontSize={'10px'} onClick={() => copyText(`v.${mlVersion}`)}>
                  v.{mlVersion}
                </Button>
              </Block>
            )}
          </Block>
        </Block>
      )}
      {!isVersionsOpen && !isGroupsOpen && (
        <>
          <Block gap="10px">
            <Block>
              <Label>Chat ID:</Label>
              <Button onClick={() => copyText(`${chatId}`)}>{chatId}</Button>
            </Block>
            {abTestConfig?.client && (
              <Block>
                <Label>FE Group:</Label>
                <Button onClick={onClick}>{abTestConfig.client.group}</Button>
              </Block>
            )}
            {abTestConfig?.ml && (
              <Block>
                <Label>ML Group:</Label>
                <Button onClick={onClick}>{abTestConfig.ml.group}</Button>
              </Block>
            )}
          </Block>

          <Button textTransform="uppercase" fontSize={'10px'} onClick={() => setIsVersionsOpen(true)}>
            versions
          </Button>
        </>
      )}
    </Wrapper>
  );
});
